<template>
  <rest-page :fetch-endpoint="fetchEndpoint">
    <page-header-new
      :title="pageTitle"
      :description="$t('ApiIntegration_Scopes_PageSubtitle')"
      :breadcrumbs="breadcrumbs"
    />
    <page-content>
      <rest-form
        ref="form"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :model="formModel"
        :rules="rules"
      >
        <div class="scopes-container" v-if="formModel">
          <form-section
            :title="$t('ApiIntegration_Scopes_QuickSelect_Title')"
            :separator="true"
            class="quick-select-section"
          >
            <div class="quick-select-buttons">
              <el-button
                type="primary"
                plain
                icon="el-icon-check"
                :disabled="allScopesChecked"
                data-qa="api-key-scopes-select-all"
                @click="setAllScopes(1)"
              >
                {{ $t('ApiIntegration_Scopes_SelectAll') }}
              </el-button>
              <el-button
                type="primary"
                plain
                icon="el-icon-close"
                :disabled="allScopesUnchecked"
                data-qa="api-key-scopes-deselect-all"
                @click="setAllScopes(0)"
              >
                {{ $t('ApiIntegration_Scopes_DeselectAll') }}
              </el-button>
            </div>
          </form-section>
          <el-row :gutter="20">
            <el-col
              :xs="24"
              :sm="24"
              :md="24"
              :lg="12"
              :xl="12"
              v-for="(group, groupKey) in scopeGroups"
              :key="groupKey"
              class="scope-group-col"
            >
              <form-section
                :title="$t(group.title)"
                :description="group.description ? $t(group.description) : ''"
                :separator="true"
              >
                <template slot="title-suffix">
                  <el-popover
                    placement="top"
                    trigger="hover"
                    popper-class="scope-info-popover"
                    :open-delay="150"
                  >
                    <div class="scope-info-content">
                      <div
                        v-for="(item, itemKey) in group.items"
                        :key="itemKey"
                        class="scope-info-item"
                      >
                        <div class="scope-info-label">{{ $t(item.label) }}</div>
                        <ul class="scope-info-endpoints">
                          <li
                            v-for="endpoint in item.endpoints"
                            :key="endpoint"
                          >
                            <code>{{ endpoint }}</code>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <i
                      slot="reference"
                      class="el-icon-question tooltip-icon"
                    ></i>
                  </el-popover>
                </template>
                <template slot="controls">
                  <el-checkbox
                    class="group-select-all"
                    :value="isGroupChecked(groupKey)"
                    :indeterminate="isGroupIndeterminate(groupKey)"
                    :data-qa="`api-key-scopes-group-${groupKey}`"
                    @change="onGroupToggle(groupKey, $event)"
                  >
                    {{ $t('ApiIntegration_Scopes_SelectAll') }}
                  </el-checkbox>
                </template>
                <div class="scope-items">
                  <div class="scope-checkboxes">
                    <checkbox
                      v-for="(item, itemKey) in group.items"
                      :key="itemKey"
                      v-model="formModel.scopes[itemKey]"
                      :title="$t(item.label)"
                      :data-qa="`api-key-scopes-item-${itemKey}`"
                      class="scope-checkbox"
                      :class="{ 'scope-checkbox-danger': itemKey === 'delete' }"
                    />
                  </div>
                </div>
              </form-section>
            </el-col>
          </el-row>
        </div>
      </rest-form>

      <div class="save-button">
        <el-button
          type="primary"
          icon="el-icon-check"
          :disabled="allScopesUnchecked"
          data-qa="api-key-scopes-save"
          @click="save()"
        >
          {{ $t('RestForm_Save') }}
        </el-button>
      </div>
    </page-content>
  </rest-page>
</template>

<script>
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import RestForm from '@/common/components/rest/RestForm'
import FormSection from '@/common/components/form/FormSection'
import Checkbox from '@/common/components/form/Checkbox'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage.vue'

export default {
  name: 'OauthKeyScopesPage',

  components: {
    RestPage,
    PageHeaderNew,
    PageContent,
    RestForm,
    FormSection,
    Checkbox
  },

  data() {
    return {
      formModel: {
        scopes: {}
      },
      keyData: null
    }
  },

  computed: {
    scopeGroups() {
      return {
        common: {
          title: 'ApiIntegration_Scopes_CommonOperations_Title',
          description: 'ApiIntegration_Scopes_CommonOperations_Description',
          items: {
            create: {
              label: 'ApiIntegration_Scopes_Create',
              endpoints: ['/api/external/create']
            },
            'batch-create': {
              label: 'ApiIntegration_Scopes_BatchCreate',
              endpoints: ['/api/external/batch-create']
            },
            update: {
              label: 'ApiIntegration_Scopes_Update',
              endpoints: ['/api/external/update']
            },
            delete: {
              label: 'ApiIntegration_Scopes_Delete',
              endpoints: ['/api/external/delete']
            },
            list: {
              label: 'ApiIntegration_Scopes_List',
              endpoints: ['/api/external/list', '/api/external/index']
            },
            info: {
              label: 'ApiIntegration_Scopes_Info',
              endpoints: ['/api/external/info']
            }
          }
        },
        campaign: {
          title: 'ApiIntegration_Scopes_CampaignOperations_Title',
          description: 'ApiIntegration_Scopes_CampaignOperations_Description',
          items: {
            'campaign:send': {
              label: 'ApiIntegration_Scopes_Send',
              endpoints: ['/api/external/send']
            },
            'campaign:execute': {
              label: 'ApiIntegration_Scopes_Execute',
              endpoints: [
                '/api/external/execute',
                '/api/external/trigger/campaign'
              ]
            },
            'campaign:start': {
              label: 'ApiIntegration_Scopes_Start',
              endpoints: ['/api/external/start']
            },
            'campaign:stop': {
              label: 'ApiIntegration_Scopes_Stop',
              endpoints: ['/api/external/stop']
            },
            'campaign:schedule': {
              label: 'ApiIntegration_Scopes_Schedule',
              endpoints: ['/api/external/schedule']
            },
            'campaign:expire-inbox': {
              label: 'ApiIntegration_Scopes_ExpireInbox',
              endpoints: ['/api/external/expire']
            },
            'message:remove-inbox': {
              label: 'ApiIntegration_Scopes_RemoveInbox',
              endpoints: ['/api/external/remove']
            },
            'campaign:cancel-journey': {
              label: 'ApiIntegration_Scopes_CancelJourney',
              endpoints: ['/api/external/cancel-journey']
            }
          }
        },
        hit: {
          title: 'ApiIntegration_Scopes_HitOperations_Title',
          description: 'ApiIntegration_Scopes_HitOperations_Description',
          items: {
            'tag:hit': {
              label: 'ApiIntegration_Scopes_Tag',
              endpoints: ['/api/external/hit/tag', '/api/external/hit/tags']
            },
            'event:hit': {
              label: 'ApiIntegration_Scopes_Event',
              endpoints: ['/api/external/hit/event', '/api/external/hit/events']
            }
          }
        },
        other: {
          title: 'ApiIntegration_Scopes_OtherOperations_Title',
          description: 'ApiIntegration_Scopes_OtherOperations_Description',
          items: {
            import: {
              label: 'ApiIntegration_Scopes_Import',
              endpoints: ['/api/external/import']
            },
            export: {
              label: 'ApiIntegration_Scopes_Export',
              endpoints: ['/api/external/segment-export/trigger']
            },
            analytics: {
              label: 'ApiIntegration_Scopes_Analytics',
              endpoints: ['/api/external/analytics']
            },
            'file:download': {
              label: 'ApiIntegration_Scopes_FileDownload',
              endpoints: ['/api/external/file/{fileId}/download']
            },
            integration: {
              label: 'ApiIntegration_Scopes_IntegrationActions_Title',
              endpoints: [
                '/api/external/otp/*',
                '/api/external/realtime-sport-updates/*',
                '/api/external/warply-updates/*'
              ]
            },
            data: {
              label: 'ApiIntegration_Scopes_DataActions_Title',
              endpoints: [
                '/api/external/join',
                '/api/external/delete-personal-data',
                '/api/external/delete-device-personal-data',
                '/api/external/merge-profile/*'
              ]
            }
          }
        }
      }
    },

    allScopeKeys() {
      return Object.values(this.scopeGroups).flatMap(group =>
        Object.keys(group.items)
      )
    },

    allScopesChecked() {
      return this.allScopeKeys.every(key => this.formModel.scopes[key] === 1)
    },

    allScopesUnchecked() {
      return this.allScopeKeys.every(key => this.formModel.scopes[key] === 0)
    },

    pageTitle() {
      return this.$t('ApiIntegration_Scopes_PageTitle', {
        keyName: (this.keyData && this.keyData.name) || ''
      })
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Settings_Breadcrumb_Settings')
        },
        {
          label: this.$t('ApiIntegration_Title'),
          route: projectRoute({
            name: 'ApiIntegration',
            query: {
              project_id: this.$route.query.project_id
            }
          })
        },
        {
          label: this.pageTitle
        }
      ]
    },

    rules() {
      return {}
    },

    saveEndpoint() {
      const keyId = this.$route.query.key_id
      if (!keyId) {
        return null
      }
      return projectEndpoint({
        method: 'patch',
        url: `oauth-keys/${keyId}/update-scopes`
      })
    },

    fetchEndpoint() {
      const keyId = this.$route.query.key_id
      const projectId = this.$route.query.project_id

      if (!keyId || !projectId) {
        return null
      }

      return {
        method: 'get',
        url: `projects/${projectId}/oauth-keys/${keyId}`,
        handler: data => {
          this.keyData = data
          this.initializeScopes(data.scopes)
          // the router sets the tab title before the key name loads, so set it here with the name
          document.title = this.pageTitle
        }
      }
    }
  },

  mounted() {
    if (!this.$route.query.key_id || !this.$route.query.project_id) {
      this.$message({
        message: this.$t('RestForm_Error_MissingParams'),
        type: 'error'
      })
      this.$router.push({
        name: 'ApiIntegration',
        query: {
          project_id: this.$route.query.project_id
        }
      })
    }
  },

  methods: {
    initializeScopes(storedScopes) {
      const scopes = {}
      const hasStoredScopes =
        storedScopes &&
        typeof storedScopes === 'object' &&
        !Array.isArray(storedScopes)
      const defaultValue = hasStoredScopes ? 0 : 1

      this.allScopeKeys.forEach(scopeKey => {
        if (
          hasStoredScopes &&
          Object.prototype.hasOwnProperty.call(storedScopes, scopeKey)
        ) {
          scopes[scopeKey] = storedScopes[scopeKey] ? 1 : 0
        } else {
          scopes[scopeKey] = defaultValue
        }
      })

      this.formModel.scopes = scopes
    },

    groupScopeKeys(groupKey) {
      return Object.keys(this.scopeGroups[groupKey].items)
    },

    isGroupChecked(groupKey) {
      return this.groupScopeKeys(groupKey).every(
        key => this.formModel.scopes[key] === 1
      )
    },

    isGroupIndeterminate(groupKey) {
      const keys = this.groupScopeKeys(groupKey)
      const checked = keys.filter(key => this.formModel.scopes[key] === 1)
        .length
      return checked > 0 && checked < keys.length
    },

    onGroupToggle(groupKey, checked) {
      const value = checked ? 1 : 0
      this.groupScopeKeys(groupKey).forEach(key => {
        this.$set(this.formModel.scopes, key, value)
      })
    },

    setAllScopes(value) {
      this.allScopeKeys.forEach(key => {
        this.$set(this.formModel.scopes, key, value)
      })
    },

    save() {
      this.$refs.form.save()
    },

    afterSave() {
      this.$router.push({
        name: 'ApiIntegration',
        query: {
          project_id: this.$route.query.project_id
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.scopes-container {
  padding: 0;
}

.quick-select-buttons {
  display: flex;
  gap: 10px;
}

.scope-items {
  padding: 0;
}

.scope-checkboxes {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 16px;
  row-gap: 24px;
}

.scope-checkbox {
  width: 100%;
}

.scope-checkbox-danger >>> .el-checkbox__label,
.scope-checkbox-danger >>> .title {
  color: #dc3545;
}

.scope-checkbox-danger >>> .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: #dc3545 !important;
  background-color: #dc3545;
}

.scope-checkbox-danger >>> .el-checkbox__inner:hover {
  border-color: #dc3545 !important;
}

.tooltip-icon {
  color: #7e8596;
  font-size: 16px;
  cursor: help;
}

>>> .el-checkbox {
  padding: 0 !important;
  width: 100%;
}

.group-select-all {
  width: auto !important;
  display: inline-flex !important;
  align-items: center;
}

.group-select-all >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.group-select-all >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  border-color: #1d87c8 !important;
  background-color: #1d87c8;
}

.group-select-all >>> .el-checkbox__inner {
  border-color: #dcdfe6 !important;
  background-color: #fff;
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.group-select-all >>> .el-checkbox__inner::after {
  height: 8px;
  border: 2px solid #fff;
  border-left: 0;
  border-top: 0;
  top: 3px;
  left: 6px;
  width: 4px;
}

.group-select-all
  >>> .el-checkbox__input.is-indeterminate
  .el-checkbox__inner::before {
  top: 8px;
  height: 3px;
}

.group-select-all >>> .el-checkbox__input {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

.group-select-all >>> .el-checkbox__label {
  color: #5c6374;
  padding-left: 10px;
  line-height: 20px;
  vertical-align: middle;
}

.save-button {
  margin: 20px 0;
  text-align: center;
}

>>> .save-button .el-button {
  width: 100%;
}

@media (max-width: 1591px) {
  .scope-group-col {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
  }
}

@media (max-width: 1200px) {
  .scope-checkboxes {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .scope-checkboxes {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .scope-checkboxes {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
.scope-info-popover.el-popover {
  max-width: 380px;
  max-height: 420px;
  overflow-y: auto;
  padding: 14px 16px;
  font-size: 12px;
  line-height: 18px;
  color: #4b515f;
}

.scope-info-item + .scope-info-item {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef0f4;
}

.scope-info-label {
  font-weight: 600;
  color: #4b515f;
  margin-bottom: 4px;
}

.scope-info-endpoints {
  list-style: none;
  padding: 0;
  margin: 0;
}

.scope-info-endpoints li {
  margin: 0;
  padding: 0;
}

.scope-info-endpoints code {
  font-family: Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 11px;
  color: #1d87c8;
  background: #f2f5f9;
  padding: 1px 4px;
  border-radius: 3px;
  word-break: break-all;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/pages/OauthKeyScopesPage.vue